import React, { useMemo } from 'react';
import {
  Badge,
  DataList,
  Flex,
  Hint,
  HoverCardContent,
  HoverCardRoot,
  HoverCardTrigger,
  Skeleton,
  Text,
} from '@/components/ui';
import { IconInfoCircle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useDateTimeViewMode } from './useDateTimeViewMode';

// Configure dayjs plugins
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

interface DateTimeCellProps {
  value?: unknown;
  format?: string; // dayjs format string (used for absolute format when not relative)
  showTime?: boolean; // for date type
  relative?: boolean; // show relative time by default (e.g., "2 hours ago")
  render?: () => React.ReactNode;
}

function DateTimeCell({
  value,
  format,
  showTime = true,
  relative = true, // Default to relative time (if not explicitly set to false)
  render,
}: DateTimeCellProps) {
  // Use global hook for synchronized view mode across all DateTimeCell components
  const { viewMode, setViewMode } = useDateTimeViewMode();

  // Determine if this cell should use global preference or forced absolute mode
  // If relative is explicitly set to false, always use absolute mode
  // Otherwise, use global preference
  const isForcedAbsolute = relative === false;
  const isRelative = isForcedAbsolute ? false : viewMode === 'relative';

  // Store original value as string for display in hover
  const originalValue = useMemo(() => {
    if (typeof value === 'string') {
      return value;
    } else if (value instanceof Date) {
      return value.toISOString();
    } else if (typeof value === 'number') {
      return new Date(value).toISOString();
    }
    return String(value);
  }, [value]);

  const date = useMemo(() => {
    if (value instanceof Date) return dayjs(value);
    if (typeof value === 'string' || typeof value === 'number') return dayjs(value);
    return null;
  }, [value]);

  const isValidDate = Boolean(date?.isValid());

  // Format date based on current mode
  const formattedDate = useMemo(() => {
    if (!isValidDate || !date) return null;
    if (isRelative) {
      return date.fromNow();
    } else if (format) {
      return date.format(format);
    } else if (showTime) {
      return date.format('MMM D, YYYY h:mm A');
    } else {
      return date.format('MMM D, YYYY');
    }
  }, [date, isRelative, format, isValidDate, showTime]);

  // Format times for hover card
  const hoverContent = useMemo(() => {
    if (!isValidDate || !date) return null;
    // Format relative time (e.g., "1 month, 7 days, 16 hours ago")
    const relativeTimeStr = date.fromNow();

    // Format UTC time - separate date and time
    const utcDate = date.utc().format('MMMM D, YYYY');
    const utcTime = date.utc().format('hh:mm:ss A');

    // Format user's local timezone - separate date and time
    const localDate = date.format('MMMM D, YYYY');
    const localTime = date.format('hh:mm:ss A');

    // Get timezone offset (e.g., +03:00 -> GMT+3)
    const offset = date.utcOffset();
    const offsetHours = Math.floor(Math.abs(offset) / 60);
    const offsetMinutes = Math.abs(offset) % 60;
    const offsetSign = offset >= 0 ? '+' : '-';
    const localTimezone = `GMT${offsetSign}${offsetHours}${offsetMinutes > 0 ? `:${offsetMinutes.toString().padStart(2, '0')}` : ''}`;

    return {
      relative: relativeTimeStr,
      original: originalValue,
      utcDate,
      utcTime,
      localDate,
      localTime,
      localTimezone,
    };
  }, [date, isValidDate, originalValue]);

  const handleClick = (e: React.MouseEvent) => {
    // Only allow toggle if not forced to absolute mode
    if (isForcedAbsolute) {
      return;
    }
    e.stopPropagation();
    const newMode = isRelative ? 'absolute' : 'relative';
    setViewMode(newMode);
  };

  const cursorStyle = isForcedAbsolute ? 'default' : 'pointer';

  if (render) {
    return <>{render()}</>;
  }

  if (!value) {
    return (
      <Text size="2xsmall" color="gray">
        —
      </Text>
    );
  }

  if (!date || !isValidDate) {
    return (
      <Text size="2xsmall" color="gray">
        Invalid date
      </Text>
    );
  }

  return (
    <HoverCardRoot>
      <HoverCardTrigger>
        <Text
          size="2xsmall"
          className="hasHoverCard"
          style={{
            cursor: cursorStyle,
            userSelect: 'none',
          }}
          onClick={handleClick}
        >
          {formattedDate || 'Invalid date'}
        </Text>
      </HoverCardTrigger>
      <HoverCardContent size="2" style={{ width: 400, maxWidth: '500px' }}>
        <Flex direction="column" gap="3">
          {/* Relative time at the top */}
          <Text
            // color="secondary"
            size="2xsmall"
            weight="medium"
            mb="2"
          >
            {hoverContent?.relative || 'Invalid date'}
          </Text>

          {/* Absolute times using DataList */}
          <DataList
            alignValues="end"
            items={[
              {
                label: <Badge size="xsmall">Original</Badge>,
                value: <Text size="2xsmall">{hoverContent?.original}</Text>,
              },
              {
                label: <Badge size="xsmall">UTC</Badge>,
                value: (
                  <Flex direction="row" gap="2" align="end">
                    <Text size="2xsmall">{hoverContent?.utcDate}</Text>
                    <Text size="2xsmall" color="secondary">
                      {hoverContent?.utcTime}
                    </Text>
                  </Flex>
                ),
              },
              {
                label: (
                  <Badge size="xsmall">{hoverContent?.localTimezone}</Badge>
                ),
                value: (
                  <Flex direction="row" gap="2" align="end" justify="between">
                    <Text size="2xsmall">{hoverContent?.localDate}</Text>
                    <Text size="2xsmall" color="secondary">
                      {hoverContent?.localTime}
                    </Text>
                  </Flex>
                ),
              },
            ]}
            orientation="horizontal"
            size="small"
          />

          {/* Hint for toggle */}
          {!isForcedAbsolute && (
            <Flex style={{ marginTop: '4px' }}>
              <Hint icon={<IconInfoCircle />} color="gray">
                Click to change view
              </Hint>
            </Flex>
          )}
        </Flex>
      </HoverCardContent>
    </HoverCardRoot>
  );
}

export function DateTimeCellSkeleton() {
  return (
    <Skeleton style={{ width: '140px', height: '16px', borderRadius: '4px' }} />
  );
}

export default DateTimeCell;
